import React from 'react';
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface CoverPageProps {
  reportData: ReportData;
}

const DEFAULT_LOGO = 'https://ctbtstbucket.blob.core.windows.net/files/a81ff888-74f3-4bd3-96b5-38cf2fde6b15.png';
const DEFAULT_COMPANY = 'Capture The Bug';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '100%',
    padding: '28mm 20mm 20mm 20mm',
    fontFamily: 'Roboto',
    backgroundColor: '#0F172A', // Premium Deep Dark Navy
  },
  sectionLabel: {
    fontSize: 8.5,
    color: '#64748B',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  dashedLogoBox: {
    borderWidth: 1,
    borderColor: '#334155',
    borderStyle: 'dashed',
    borderRadius: 6,
    width: 150,
    height: 42,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  dashedBoxText: {
    color: '#94A3B8',
    fontSize: 10,
    fontWeight: 'bold',
    fontStyle: 'italic',
    letterSpacing: 0.5,
  },
  logoImage: {
    width: 130,
    height: 30,
    objectFit: 'contain',
  },
  preparedByName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#3B82F6', // Branded light blue/indigo
    marginTop: 2,
  },
  bodyContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 40,
  },
  confidentialPill: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderWidth: 1.2,
    borderColor: '#EF4444',
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 3.5,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  confidentialText: {
    color: '#FCA5A5',
    fontSize: 8.5,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  reportTitle: {
    fontSize: 34,
    fontWeight: 'bold',
    color: '#FFFFFF',
    lineHeight: 1.15,
    marginBottom: 16,
  },
  typePill: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderWidth: 1.2,
    borderColor: '#3B82F6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 5,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  typeText: {
    color: '#93C5FD',
    fontSize: 10.5,
    fontWeight: 'bold',
  },
  testingPeriodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  testingPeriodLabel: {
    fontSize: 9,
    color: '#64748B',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginRight: 6,
  },
  testingPeriodValue: {
    fontSize: 10.5,
    color: '#E2E8F0',
    fontWeight: 'bold',
  },
  bottomContainer: {
    flexDirection: 'column',
    marginTop: 'auto',
  },
  clientName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: 2,
    marginBottom: 18,
  },
  dividerLine: {
    borderBottomWidth: 1.2,
    borderBottomColor: '#1E293B',
    width: '100%',
    marginBottom: 12,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 9.5,
    color: '#64748B',
    fontWeight: 'medium',
  },
});

const CoverPageV2: React.FC<CoverPageProps> = ({ reportData }) => {
  const formatDateString = (d: any) => {
    if (!d) return '';
    try {
      const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
      const date = new Date(d);
      return date.toLocaleDateString('en-GB', options); // returns e.g. "18 Jun 2026"
    } catch (e) {
      return String(d);
    }
  };

  const formattedStart = formatDateString(reportData.start_date);
  const formattedEnd = formatDateString(reportData.end_date);

  const testingPeriod = formattedStart && formattedEnd
    ? `${formattedStart} - ${formattedEnd}`
    : formatDateString(reportData.current_date) || 'N/A';

  // Extract type of pentest (black-box, grey-box, white-box) or dynamically build from target list
  const getAutoPentestType = () => {
    const targets = reportData.target_details || [];
    if (targets.length === 0) {
      return 'Grey-box Web Application';
    }

    const types = new Set<string>();
    const isNetworkTarget = (t: any) => {
      const type = (t.target_type || t.type || '').toLowerCase();
      const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
      if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
        return false;
      }
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return false;
      }
      return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
             !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
             /\/\s*\d{1,2}\s*$/.test(value);
    };

    targets.forEach(t => {
      const typeStr = (t.target_type || t.type || '').toLowerCase();
      if (isNetworkTarget(t)) {
        types.add('Network Infrastructure');
      } else if (typeStr.includes('api')) {
        types.add('API');
      } else if (typeStr.includes('mobile') || typeStr.includes('ios') || typeStr.includes('android')) {
        types.add('Mobile Application');
      } else {
        types.add('Web Application');
      }
    });

    const joinedTypes = Array.from(types).join(' & ');
    return `Grey-box ${joinedTypes}`;
  };

  const pentestType = reportData.testing_type || reportData.program_details?.[0]?.testing_type || getAutoPentestType();
  const brandingName = reportData.branding_company || DEFAULT_COMPANY;
  const clientName = reportData.company_name || 'Client';

  return (
    <Page size="A4" style={styles.page}>
      {/* Top Section - Prepared By */}
      <View>
        <Text style={styles.sectionLabel}>Prepared By</Text>
        <View style={styles.dashedLogoBox}>
          {reportData.branding_logo && !reportData.branding_logo.includes('i.postimg.cc') ? (
            <Image src={reportData.branding_logo} style={styles.logoImage} />
          ) : (
            <Image src={DEFAULT_LOGO} style={styles.logoImage} />
          )}
        </View>
        <Text style={styles.preparedByName}>{brandingName}</Text>
      </View>

      {/* Main Title Section */}
      <View style={styles.bodyContainer}>
        {/* Confidential Badge */}
        <View style={styles.confidentialPill}>
          <Text style={styles.confidentialText}>Confidential</Text>
        </View>

        {/* Report Title */}
        <Text style={styles.reportTitle}>{reportData.report_title || 'Penetration Test Report'}</Text>

        {/* Pentest Type Pill */}
        <View style={styles.typePill}>
          <Text style={styles.typeText}>{pentestType}</Text>
        </View>

        {/* Testing Period */}
        <View style={styles.testingPeriodRow}>
          <Text style={styles.testingPeriodLabel}>Testing Period</Text>
          <Text style={styles.testingPeriodValue}>{testingPeriod}</Text>
        </View>
      </View>

      {/* Bottom Section - Organization Assessed */}
      <View style={styles.bottomContainer}>
        <Text style={styles.sectionLabel}>Organization Assessed</Text>
        <View style={styles.dashedLogoBox}>
          {reportData.client_logo || reportData.company_logo ? (
            <Image src={reportData.client_logo || reportData.company_logo} style={styles.logoImage} />
          ) : (
            <Text style={styles.dashedBoxText}>{clientName.toUpperCase()}</Text>
          )}
        </View>
        <Text style={styles.clientName}>{clientName}</Text>

        {/* Divider & Footer Metadata */}
        <View style={styles.dividerLine} />
        <View style={styles.footerRow}>
          <Text style={styles.footerText}>
            Report v{(() => {
              const cleaned = (reportData.version_number || '1.0').replace(/^[vV]+/, '').trim();
              return /^\d+$/.test(cleaned) ? `${cleaned}.0` : cleaned;
            })()}
          </Text>
          <Text style={styles.footerText}>Prepared {formatDateString(reportData.current_date)}</Text>
        </View>
      </View>
    </Page>
  );
};

export default CoverPageV2;
